import { parseTemplate } from 'url-template';

import { type AddPrefix, type RemovePrefix } from '../../types';

import {
  type AppName,
  type AppRequest,
  type GetAppRequestBody,
  type GetRequestMethodPaths,
  type HttpMethod,
  type TransformedAppRequest,
  type TransformerFn,
} from './query-builder.types';

const encodeUrl = ({
  url,
  variables,
}: {
  url: string;
  variables: Record<string, string | number>;
}) => {
  const urlTemplate = parseTemplate(url);
  return urlTemplate.expand(variables);
};

/**
 * A factory function that returns an app-specific `queryBuilder` function
 * for use in building RTK Query endpoint.
 *
 * ## Usage
 *
 * This function takes 2 generic arguments:
 *
 * ```ts
 * const appQueryBuidler = createAppQueryBuilder<App, PathPrefix>()
 * ```
 *
 * - `App`: The name of the app (e.g., 'assortment', 'product', etc.)
 * - `PathPrefix`: An optional prefix for the path (e.g., 'v1', 'v2', etc.)
 *
 * Use `appQueryBuilder` to enforce **full typesafety** to your endpoints.
 */
export const createAppQueryBuilder = <
  App extends AppName,
  PathPrefix extends string = '',
>() => {
  type GetPrefixedMethodPaths<M extends HttpMethod, P extends string> = Extract<
    GetRequestMethodPaths<App, M>,
    AddPrefix<P, PathPrefix>
  >;

  /**
   * The `queryBuilder` function is a higher-order function that takes
   * an HTTP method and a corresponding URL path as arguments to return an
   * app-specific RTK Query `queryFn`. The internal mechanism of all
   * `queryFns` is identical. However, the types derived from the factory
   * will enforce a strongly-typed interface for individual endpoints.
   * This means only valid combinations of `method` and `endpointPath` for
   * the selected `App` will compile.
   *
   * ## Optional Transformer Parameter
   *
   * `queryBuilder` exposes an optional `transformer` parameter which you can use
   * to transform the endpoint's request `body` using a function. When using `transformer`,
   * your expected `body` will be set to the input of the `transformer` while the
   * transformation result is checked against the type expected by the endpoint.
   * This check is an `IsEqual` assertion which means your transformation needs
   * to match the expected `body` exactly (it must not subtype the request which would
   * typically pass with a naive `extends` check). This is because BE rejects the subtyping
   * of product types (e.g., objects with additional fields).
   *
   * ## Usage
   *
   * ```ts
   * // query endpoint
   * builder.query({
   *  query: appQueryBuilder('get', '/products'),
   *  ...
   * })
   *
   * // mutation endpoint
   * builder.mutation({
   *  query: appQueryBuilder('patch', '/products'),
   *  ...
   * })
   *
   * // with transformer
   * const transformer = (body: any) => {
   *  return { ...body };
   * };
   *
   * builder.mutation({
   *  query: appQueryBuilder('post', '/products', transformer),
   *  ...
   * })
   * ```
   *
   * @param method - The HTTP method (e.g., 'get', 'post', etc.)
   * @param endpointPath - The URL path (e.g., '/products', '/filters', etc.)
   * @param transformer - An optional transformer function to transform the request `body`
   */
  function queryBuilder<
    M extends HttpMethod,
    P extends RemovePrefix<GetRequestMethodPaths<App, M>, PathPrefix>,
  >(
    method: M,
    endpointPath: P,
  ): (args: AppRequest<App, M, GetPrefixedMethodPaths<M, P>>) => {
    url: string;
    method: Uppercase<M>;
    body: GetAppRequestBody<AppRequest<App, M, GetPrefixedMethodPaths<M, P>>>;
    extraArgs?: Record<string, unknown>;
  };

  function queryBuilder<
    M extends HttpMethod,
    P extends RemovePrefix<GetRequestMethodPaths<App, M>, PathPrefix>,
    Transformer extends TransformerFn<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      GetAppRequestBody<AppRequest<App, M, GetPrefixedMethodPaths<M, P>>>
    >,
  >(
    method: M,
    endpointPath: P,
    transformer: Transformer,
  ): (
    args: TransformedAppRequest<
      App,
      M,
      GetPrefixedMethodPaths<M, P>,
      Transformer
    >,
  ) => {
    url: string;
    method: Uppercase<M>;
    body: GetAppRequestBody<AppRequest<App, M, GetPrefixedMethodPaths<M, P>>>;
    extraArgs?: Record<string, unknown>;
  };

  function queryBuilder<
    M extends HttpMethod,
    P extends RemovePrefix<GetRequestMethodPaths<App, M>, PathPrefix>,
    Transformer extends TransformerFn<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      GetAppRequestBody<AppRequest<App, M, GetPrefixedMethodPaths<M, P>>>
    >,
  >(method: M, endpointPath: P, transformer?: Transformer) {
    type OriginalArgs = AppRequest<App, M, GetPrefixedMethodPaths<M, P>>;
    type QueryFnArgs = Transformer extends undefined
      ? OriginalArgs
      : TransformedAppRequest<
          App,
          M,
          GetPrefixedMethodPaths<M, P>,
          Transformer
        >;

    /**
     * `queryFn` is the actual function that formats the endpoint's request
     * parameters into the RTK Query `BaseQuery` args object. As with `queryBuilder`,
     * the interface of `queryFn` is strongly typed to ensure that your RTK
     * endpoint `QueryArg` declarations align with the types expected by the
     * endpoint declared for `queryBuilder`.
     */
    function queryFn(args: QueryFnArgs) {
      const searchParams = new URLSearchParams();
      if (
        'query' in args &&
        typeof args.query === 'object' &&
        args.query !== null
      ) {
        const { query } = args;
        for (const [queryParamName, queryParam] of Object.entries(query)) {
          if (
            typeof queryParam === 'number' ||
            typeof queryParam === 'string' ||
            typeof queryParam === 'boolean'
          ) {
            searchParams.append(queryParamName, queryParam.toString());
          } else if (Array.isArray(queryParam)) {
            for (const queryParamValue of queryParam) {
              if (queryParamValue !== null) {
                searchParams.append(queryParamName, queryParamValue.toString());
              }
            }
          } else if (typeof queryParam === 'undefined' || queryParam === null) {
            continue;
          } else {
            throw new Error(
              `query parameter type ${typeof queryParam} is not supported`,
            );
          }
        }
      }

      const path = 'path' in args ? args.path : {};
      const extraArgs = 'extraArgs' in args ? args.extraArgs : undefined;

      const body = (() => {
        if (!('body' in args)) {
          return undefined as never;
        }
        if (transformer) {
          return transformer(args.body);
        }
        return args.body as GetAppRequestBody<OriginalArgs>;
      })();

      return {
        url:
          encodeUrl({
            url: endpointPath,
            variables: path as Record<string, string | number>,
          }) + (searchParams.toString() ? `?${searchParams.toString()}` : ''),
        method: method.toUpperCase() as Uppercase<M>,
        body,
        extraArgs,
      };
    }

    return queryFn;
  }

  return queryBuilder;
};
